import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  staff: any | null;
}

const EditSystemStaffDialog = ({ open, onOpenChange, staff }: Props) => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: "", phone: "", role: "support", department: "general", notes: "", status: "active",
  });

  useEffect(() => {
    if (staff) {
      setForm({
        full_name: staff.full_name || "",
        phone: staff.phone || "",
        role: staff.role || "support",
        department: staff.department || "general",
        notes: staff.notes || "",
        status: staff.status || "active",
      });
    }
  }, [staff]);

  if (!staff) return null;

  const u = (key: string, val: string) => setForm({ ...form, [key]: val });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const { data, error } = await supabase.functions.invoke("manage-system-staff", {
      body: { action: "update", staff_id: staff.id, ...form },
    });

    setLoading(false);
    if (error || data?.error) {
      toast.error(data?.error || error?.message || "خطأ");
      return;
    }
    toast.success("تم تحديث بيانات الموظف ✅");
    queryClient.invalidateQueries({ queryKey: ["system-staff"] });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="w-5 h-5" />
            تعديل بيانات: {staff.full_name}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>الاسم الكامل</Label>
            <Input value={form.full_name} onChange={(e) => u("full_name", e.target.value)} required />
          </div>
          <div>
            <Label>رقم الهاتف</Label>
            <Input value={form.phone} onChange={(e) => u("phone", e.target.value)} dir="ltr" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>الدور</Label>
              <Select value={form.role} onValueChange={(v) => u("role", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="support">دعم فني</SelectItem>
                  <SelectItem value="call_center">مركز اتصال</SelectItem>
                  <SelectItem value="accountant">محاسب</SelectItem>
                  <SelectItem value="developer">مطوّر</SelectItem>
                  <SelectItem value="supervisor">مشرف</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>القسم</Label>
              <Select value={form.department} onValueChange={(v) => u("department", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">عام</SelectItem>
                  <SelectItem value="technical">تقني</SelectItem>
                  <SelectItem value="financial">مالي</SelectItem>
                  <SelectItem value="operations">عمليات</SelectItem>
                  <SelectItem value="customer_service">خدمة العملاء</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>الحالة</Label>
            <Select value={form.status} onValueChange={(v) => u("status", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="active">نشط</SelectItem>
                <SelectItem value="inactive">معطّل</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>ملاحظات</Label>
            <Textarea value={form.notes} onChange={(e) => u("notes", e.target.value)} rows={2} />
          </div>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "حفظ التعديلات"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditSystemStaffDialog;
